/**
 * LinkStatusPill — small header widget that shows Ableton Link status:
 *   • Disconnected → grey pill inviting the DJ to install the Bridge
 *   • Connected + 0 peers → cyan pill: "Bridge ready · no peers yet"
 *   • Connected + N peers → green pill with peer count + session BPM
 *
 * Also carries the "Follow BPM" toggle — when on, Studio.jsx will drive
 * the active deck's pitch to match the Link session tempo in real time.
 */
import React from "react";
import { Radio, Zap } from "lucide-react";
import { useLink } from "@/context/LinkContext";

export default function LinkStatusPill({ follow, onToggleFollow }) {
  const { connected, sessionBpm, peersCount } = useLink();

  if (!connected) {
    return (
      <a href="/download" data-testid="link-status-disconnected"
         title="Install the NeuralDeck Bridge to sync tempo with Serato / Traktor / Rekordbox / Ableton Live"
         className="hidden lg:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[#1E2638] text-[10px] font-mono uppercase tracking-widest text-[#94A3B8] hover:text-white hover:border-[#00F0FF] transition">
        <Radio className="w-3 h-3 opacity-40"/> Link · off
      </a>
    );
  }

  const color = peersCount > 0 ? "#10B981" : "#00F0FF";
  const label = peersCount > 0
    ? `Link · ${peersCount} peer${peersCount > 1 ? "s" : ""} · ${sessionBpm?.toFixed(1) ?? "--"} BPM`
    : "Link · bridge ready";

  return (
    <div className="inline-flex items-center gap-2" data-testid="link-status-connected">
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] font-mono uppercase tracking-widest"
            style={{ borderColor: color, color }}>
        <Radio className="w-3 h-3 animate-pulse"/> {label}
      </span>
      {peersCount > 0 && (
        <button type="button"
                data-testid="link-follow-toggle"
                onClick={onToggleFollow}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-[10px] font-mono uppercase tracking-widest transition ${
                  follow ? "bg-[#00F0FF] text-black border-[#00F0FF]" : "text-[#00F0FF] border-[#00F0FF]/40 hover:border-[#00F0FF]"
                }`}
                title="Auto-adjust the active deck's pitch to match the Link session BPM">
          <Zap className="w-3 h-3"/> {follow ? "Following" : "Follow BPM"}
        </button>
      )}
    </div>
  );
}
